import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Maximize, Minimize, Users, TrendingUp, Target, Zap, Globe, DollarSign, BarChart3, Rocket, Heart, Brain, Video, Clock, BookOpen, Lightbulb } from "lucide-react";

const slides = [
  // 1 — Title
  {
    id: "title",
    render: () => (
      <div className="flex flex-col items-center justify-center h-full bg-gradient-to-br from-[hsl(328,85%,12%)] via-[hsl(280,40%,15%)] to-[hsl(193,90%,10%)] text-white px-16 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-[10%] left-[15%] w-[400px] h-[400px] rounded-full bg-[hsl(328,85%,50%)] opacity-[0.08] blur-[120px]" />
          <div className="absolute bottom-[15%] right-[10%] w-[500px] h-[500px] rounded-full bg-[hsl(193,90%,45%)] opacity-[0.06] blur-[140px]" />
        </div>
        <div className="relative z-10 text-center">
          <div className="inline-block px-5 py-2 rounded-full border border-white/20 text-sm tracking-[0.2em] uppercase text-white/70 mb-10">Investor Deck · 2026</div>
          <h1 className="text-[96px] font-black leading-[0.9] tracking-tight mb-6">
            <span className="bg-gradient-to-r from-[hsl(328,85%,60%)] to-[hsl(193,90%,55%)] bg-clip-text text-transparent">CSW</span>
          </h1>
          <p className="text-3xl font-light text-white/80 mb-2">Chill Study Work</p>
          <p className="text-xl text-white/50 max-w-[600px] mx-auto mt-8">The community-powered productivity platform where people study, focus, and work — together.</p>
        </div>
      </div>
    ),
  },
  // 2 — Problem
  {
    id: "problem",
    render: () => (
      <div className="flex flex-col justify-center h-full bg-[hsl(0,0%,3%)] text-white px-20 relative">
        <p className="text-sm uppercase tracking-[0.25em] text-[hsl(328,85%,60%)] mb-8 font-semibold">The Problem</p>
        <h2 className="text-[56px] font-bold leading-[1.1] mb-14 max-w-[900px]">Studying alone is <span className="text-[hsl(328,85%,60%)]">broken</span>.</h2>
        <div className="grid grid-cols-3 gap-10">
          {[
            { icon: Heart, stat: "75%", label: "of remote students report feeling isolated and disconnected" },
            { icon: Clock, stat: "40%", label: "of study time is lost to procrastination without accountability" },
            { icon: Brain, stat: "$8B+", label: "productivity tool market — yet focus rates keep declining" },
          ].map((item) => (
            <div key={item.stat} className="bg-white/[0.04] border border-white/10 rounded-2xl p-8">
              <item.icon className="w-8 h-8 text-[hsl(193,90%,55%)] mb-5" />
              <p className="text-[48px] font-black text-white mb-3">{item.stat}</p>
              <p className="text-white/60 text-base leading-relaxed">{item.label}</p>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  // 3 — Solution
  {
    id: "solution",
    render: () => (
      <div className="flex flex-col justify-center h-full bg-white px-20 relative">
        <p className="text-sm uppercase tracking-[0.25em] text-[hsl(328,85%,50%)] mb-6 font-semibold">Our Solution</p>
        <h2 className="text-[52px] font-bold leading-[1.1] text-[hsl(240,20%,15%)] mb-6 max-w-[850px]">Body doubling meets <span className="text-[hsl(193,90%,40%)]">modern productivity</span></h2>
        <p className="text-xl text-[hsl(240,10%,40%)] max-w-[750px] mb-14 leading-relaxed">CSW leverages the scientifically-proven concept of social facilitation — people focus better when others are present. We make this accessible 24/7, globally, and completely free.</p>
        <div className="grid grid-cols-4 gap-6">
          {[
            { icon: Video, title: "Study Streams", desc: "Live camera-on rooms for visual accountability" },
            { icon: Clock, title: "Pomodoro Timer", desc: "Synchronized sessions with shared breaks" },
            { icon: BookOpen, title: "Flashcards & Quizzes", desc: "AI-powered study tools, always free" },
            { icon: Users, title: "Global Community", desc: "24/7 study partners from around the world" },
          ].map((f) => (
            <div key={f.title} className="bg-[hsl(240,5%,97%)] border border-[hsl(240,8%,90%)] rounded-xl p-6">
              <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[hsl(328,85%,50%)] to-[hsl(193,90%,45%)] flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-bold text-[hsl(240,20%,15%)] mb-2 text-lg">{f.title}</h3>
              <p className="text-sm text-[hsl(240,10%,45%)] leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  // 4 — Market
  {
    id: "market",
    render: () => (
      <div className="flex flex-col justify-center h-full bg-[hsl(240,20%,15%)] text-white px-20 relative">
        <p className="text-sm uppercase tracking-[0.25em] text-[hsl(193,90%,55%)] mb-8 font-semibold">Market Opportunity</p>
        <h2 className="text-[52px] font-bold leading-[1.1] mb-14">A massive, growing market</h2>
        <div className="grid grid-cols-2 gap-12 items-start">
          <div className="space-y-8">
            <div className="bg-white/[0.05] border border-white/10 rounded-2xl p-8">
              <p className="text-sm text-white/50 uppercase tracking-wider mb-2">TAM</p>
              <p className="text-[56px] font-black text-[hsl(193,90%,55%)]">$8.2B</p>
              <p className="text-white/60 mt-2">Global EdTech productivity tools market</p>
            </div>
            <div className="bg-white/[0.05] border border-white/10 rounded-2xl p-8">
              <p className="text-sm text-white/50 uppercase tracking-wider mb-2">SAM</p>
              <p className="text-[56px] font-black text-[hsl(328,85%,60%)]">$1.5B</p>
              <p className="text-white/60 mt-2">Online study & accountability platforms</p>
            </div>
          </div>
          <div className="space-y-6">
            <h3 className="text-2xl font-semibold mb-4">Key Tailwinds</h3>
            {[
              "Remote & hybrid learning is the new norm",
              "Gen Z demands social, community-driven tools",
              "ADHD awareness driving body doubling demand",
              "Creator economy fueling 'Study With Me' trend",
              "K-pop study culture expanding globally",
            ].map((t) => (
              <div key={t} className="flex items-start gap-4">
                <div className="w-2 h-2 rounded-full bg-[hsl(193,90%,55%)] mt-2 shrink-0" />
                <p className="text-white/70 text-lg">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    ),
  },
  // 5 — Traction
  {
    id: "traction",
    render: () => (
      <div className="flex flex-col justify-center h-full bg-white px-20 relative">
        <p className="text-sm uppercase tracking-[0.25em] text-[hsl(328,85%,50%)] mb-8 font-semibold">Traction</p>
        <h2 className="text-[52px] font-bold leading-[1.1] text-[hsl(240,20%,15%)] mb-14">Growing fast, growing organically</h2>
        <div className="grid grid-cols-4 gap-6">
          {[
            { stat: "3,000+", label: "5-Star Ratings", icon: TrendingUp },
            { stat: "24/7", label: "Global Availability", icon: Globe },
            { stat: "$0", label: "User Cost", icon: DollarSign },
            { stat: "∞", label: "Free Streams", icon: Zap },
          ].map((item) => (
            <div key={item.label} className="text-center bg-gradient-to-b from-[hsl(240,5%,97%)] to-white border border-[hsl(240,8%,90%)] rounded-2xl p-8">
              <item.icon className="w-8 h-8 text-[hsl(328,85%,50%)] mx-auto mb-4" />
              <p className="text-[44px] font-black text-[hsl(240,20%,15%)]">{item.stat}</p>
              <p className="text-[hsl(240,10%,45%)] mt-2 font-medium">{item.label}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 bg-gradient-to-r from-[hsl(328,85%,50%)] to-[hsl(193,90%,45%)] rounded-2xl p-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold opacity-90">Organic growth through SEO + community</p>
              <p className="text-sm opacity-70 mt-1">200+ optimized pages covering study streams, K-pop study, university rooms & more</p>
            </div>
            <BarChart3 className="w-12 h-12 opacity-40" />
          </div>
        </div>
      </div>
    ),
  },
  // 6 — Product
  {
    id: "product",
    render: () => (
      <div className="flex flex-col justify-center h-full bg-[hsl(0,0%,3%)] text-white px-20 relative">
        <p className="text-sm uppercase tracking-[0.25em] text-[hsl(193,90%,55%)] mb-8 font-semibold">Product Ecosystem</p>
        <h2 className="text-[48px] font-bold leading-[1.1] mb-14">All-in-one study & productivity hub</h2>
        <div className="grid grid-cols-3 gap-6">
          {[
            { title: "Study Streams", desc: "Live video rooms with cameras on, shared timers, ritual check-ins. Visual accountability 24/7.", color: "hsl(328,85%,50%)" },
            { title: "Body Doubling", desc: "Science-backed social facilitation. Constant presence provides solidarity and drives deep focus.", color: "hsl(193,90%,45%)" },
            { title: "Pomodoro Timer", desc: "Synchronized group sessions with structured breaks. Visible accountability with shared progress.", color: "hsl(280,60%,55%)" },
            { title: "Task Management", desc: "Organize your workflow with collaborative task boards. Set goals and track completion.", color: "hsl(328,85%,50%)" },
            { title: "AI Flashcards", desc: "Generate study materials instantly with AI. Spaced repetition and interactive quizzes.", color: "hsl(193,90%,45%)" },
            { title: "Study Rooms", desc: "University-specific virtual libraries. Join peers from 50+ universities worldwide.", color: "hsl(280,60%,55%)" },
          ].map((f) => (
            <div key={f.title} className="bg-white/[0.04] border border-white/10 rounded-xl p-6 hover:border-white/20 transition-colors">
              <div className="w-3 h-3 rounded-full mb-4" style={{ backgroundColor: f.color }} />
              <h3 className="text-lg font-bold mb-2">{f.title}</h3>
              <p className="text-white/50 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  // 7 — Competitive Landscape
  {
    id: "competition",
    render: () => (
      <div className="flex flex-col justify-center h-full bg-white px-20 relative">
        <p className="text-sm uppercase tracking-[0.25em] text-[hsl(328,85%,50%)] mb-8 font-semibold">Competitive Landscape</p>
        <h2 className="text-[48px] font-bold leading-[1.1] text-[hsl(240,20%,15%)] mb-10">Why CSW wins</h2>
        <div className="overflow-hidden rounded-2xl border border-[hsl(240,8%,90%)]">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-[hsl(240,20%,15%)] text-white">
                <th className="px-6 py-4 text-sm font-semibold w-[200px]">Feature</th>
                <th className="px-6 py-4 text-sm font-semibold text-center">CSW</th>
                <th className="px-6 py-4 text-sm font-semibold text-center">Focusmate</th>
                <th className="px-6 py-4 text-sm font-semibold text-center">StudyStream</th>
                <th className="px-6 py-4 text-sm font-semibold text-center">StudyTogether</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {[
                { feature: "Free unlimited sessions", csw: true, f: false, ss: false, st: true },
                { feature: "Live video streams", csw: true, f: true, ss: true, st: false },
                { feature: "AI flashcards & quizzes", csw: true, f: false, ss: false, st: false },
                { feature: "Pomodoro timer", csw: true, f: false, ss: true, st: true },
                { feature: "Task management", csw: true, f: false, ss: false, st: false },
                { feature: "Body doubling science", csw: true, f: true, ss: false, st: false },
                { feature: "University study rooms", csw: true, f: false, ss: false, st: false },
              ].map((row, i) => (
                <tr key={row.feature} className={i % 2 === 0 ? "bg-[hsl(240,5%,97%)]" : "bg-white"}>
                  <td className="px-6 py-3 font-medium text-[hsl(240,20%,15%)]">{row.feature}</td>
                  {[row.csw, row.f, row.ss, row.st].map((v, j) => (
                    <td key={j} className="px-6 py-3 text-center">
                      <span className={`inline-block w-6 h-6 rounded-full text-xs font-bold leading-6 ${v ? "bg-[hsl(150,60%,45%)] text-white" : "bg-[hsl(0,0%,88%)] text-[hsl(0,0%,55%)]"}`}>
                        {v ? "✓" : "—"}
                      </span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ),
  },
  // 8 — Business Model
  {
    id: "business-model",
    render: () => (
      <div className="flex flex-col justify-center h-full bg-[hsl(240,20%,15%)] text-white px-20 relative">
        <p className="text-sm uppercase tracking-[0.25em] text-[hsl(193,90%,55%)] mb-8 font-semibold">Business Model</p>
        <h2 className="text-[52px] font-bold leading-[1.1] mb-14">Free core, premium upside</h2>
        <div className="grid grid-cols-2 gap-12">
          <div className="space-y-6">
            <h3 className="text-2xl font-semibold text-[hsl(193,90%,55%)] mb-6">Free Tier (Growth Engine)</h3>
            {["Unlimited study streams", "Pomodoro timer & task management", "Community study rooms", "Basic flashcards & quizzes"].map((t) => (
              <div key={t} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-[hsl(193,90%,45%)] flex items-center justify-center text-xs font-bold">✓</div>
                <p className="text-white/80">{t}</p>
              </div>
            ))}
          </div>
          <div className="space-y-6">
            <h3 className="text-2xl font-semibold text-[hsl(328,85%,60%)] mb-6">Premium Tier (Revenue)</h3>
            {["AI-powered study tools & analytics", "Private group rooms with custom branding", "Advanced progress tracking & insights", "Priority access to new features"].map((t) => (
              <div key={t} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-[hsl(328,85%,50%)] flex items-center justify-center text-xs font-bold">✓</div>
                <p className="text-white/80">{t}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-12 grid grid-cols-3 gap-6">
          {[
            { label: "Target ARPU", value: "$9.99/mo" },
            { label: "Conversion Target", value: "5–8%" },
            { label: "Additional Revenue", value: "University partnerships" },
          ].map((m) => (
            <div key={m.label} className="bg-white/[0.05] border border-white/10 rounded-xl p-6 text-center">
              <p className="text-sm text-white/50 mb-2">{m.label}</p>
              <p className="text-xl font-bold">{m.value}</p>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  // 9 — The Ask
  {
    id: "ask",
    render: () => (
      <div className="flex flex-col justify-center h-full bg-white px-20 relative">
        <p className="text-sm uppercase tracking-[0.25em] text-[hsl(328,85%,50%)] mb-8 font-semibold">The Ask</p>
        <h2 className="text-[52px] font-bold leading-[1.1] text-[hsl(240,20%,15%)] mb-10">Raising to scale</h2>
        <div className="grid grid-cols-3 gap-8 mb-14">
          {[
            { icon: Rocket, title: "Product", desc: "Scale infrastructure, ship mobile apps, expand AI study tools" },
            { icon: Users, title: "Team", desc: "Hire engineering, community, and growth leads" },
            { icon: Globe, title: "Expansion", desc: "Localize for Korea, Japan, Vietnam, and LATAM markets" },
          ].map((item) => (
            <div key={item.title} className="bg-gradient-to-b from-[hsl(240,5%,97%)] to-white border border-[hsl(240,8%,90%)] rounded-2xl p-8">
              <item.icon className="w-10 h-10 text-[hsl(328,85%,50%)] mb-5" />
              <h3 className="text-xl font-bold text-[hsl(240,20%,15%)] mb-3">{item.title}</h3>
              <p className="text-[hsl(240,10%,45%)] leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
        <div className="bg-gradient-to-r from-[hsl(328,85%,50%)] to-[hsl(193,90%,45%)] rounded-2xl p-10 text-white text-center">
          <p className="text-lg opacity-80 mb-2">Use of Funds</p>
          <div className="flex justify-center gap-16 mt-4">
            {[
              { pct: "45%", label: "Product & Engineering" },
              { pct: "25%", label: "Growth & Marketing" },
              { pct: "20%", label: "Operations & Team" },
              { pct: "10%", label: "Localization" },
            ].map((u) => (
              <div key={u.label}>
                <p className="text-3xl font-black">{u.pct}</p>
                <p className="text-sm opacity-70 mt-1">{u.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    ),
  },
  // 10 — Closing
  {
    id: "closing",
    render: () => (
      <div className="flex flex-col items-center justify-center h-full bg-gradient-to-br from-[hsl(328,85%,12%)] via-[hsl(280,40%,15%)] to-[hsl(193,90%,10%)] text-white px-16 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute bottom-[20%] left-[20%] w-[600px] h-[600px] rounded-full bg-[hsl(328,85%,50%)] opacity-[0.06] blur-[150px]" />
          <div className="absolute top-[20%] right-[15%] w-[400px] h-[400px] rounded-full bg-[hsl(193,90%,45%)] opacity-[0.08] blur-[120px]" />
        </div>
        <div className="relative z-10 text-center">
          <h2 className="text-[72px] font-black leading-[1] mb-6">
            <span className="bg-gradient-to-r from-[hsl(328,85%,60%)] to-[hsl(193,90%,55%)] bg-clip-text text-transparent">Study Together.</span><br />
            <span className="text-white">Focus Together.</span>
          </h2>
          <p className="text-2xl text-white/60 mt-8 mb-12">Let's build the future of collaborative productivity.</p>
          <div className="inline-block px-8 py-4 rounded-full bg-white/10 border border-white/20 text-lg">
            csw.live
          </div>
        </div>
      </div>
    ),
  },
];

const PitchDeck = () => {
  const [current, setCurrent] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const next = useCallback(() => setCurrent((c) => Math.min(c + 1, slides.length - 1)), []);
  const prev = useCallback(() => setCurrent((c) => Math.max(c - 1, 0)), []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); next(); }
      if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
      if (e.key === "Escape") setIsFullscreen(false);
      if (e.key === "f" || e.key === "F") setIsFullscreen((v) => !v);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [next, prev]);

  const containerClass = isFullscreen
    ? "fixed inset-0 z-[9999] bg-black flex flex-col"
    : "min-h-screen bg-[hsl(0,0%,6%)] flex flex-col items-center justify-center p-4 sm:p-8";

  return (
    <div className={containerClass}>
      {/* Slide */}
      <div className={`relative ${isFullscreen ? "flex-1 flex items-center justify-center" : "w-full max-w-[1100px]"}`}>
        <div className={`relative ${isFullscreen ? "" : "rounded-xl overflow-hidden shadow-2xl"}`}
          style={{ aspectRatio: "16/9", width: isFullscreen ? `min(100vw, calc(100vh * 16 / 9))` : "100%" }}>
          <div className="absolute inset-0">
            {slides[current].render()}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className={`flex items-center gap-4 mt-6 ${isFullscreen ? "absolute bottom-6 left-1/2 -translate-x-1/2 z-[10000]" : ""}`}>
        <button onClick={prev} disabled={current === 0}
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 flex items-center justify-center text-white transition-colors">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <span className="text-white/60 text-sm font-mono min-w-[60px] text-center">
          {current + 1} / {slides.length}
        </span>
        <button onClick={next} disabled={current === slides.length - 1}
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 flex items-center justify-center text-white transition-colors">
          <ChevronRight className="w-5 h-5" />
        </button>
        <button onClick={() => setIsFullscreen((v) => !v)}
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors ml-2">
          {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
        </button>
      </div>

      {/* Slide thumbnails */}
      {!isFullscreen && (
        <div className="flex gap-2 mt-6 overflow-x-auto pb-2 max-w-[1100px] w-full">
          {slides.map((s, i) => (
            <button key={s.id} onClick={() => setCurrent(i)}
              className={`shrink-0 w-20 h-[45px] rounded-md border-2 transition-all text-[6px] font-bold text-white/60 ${i === current ? "border-[hsl(328,85%,50%)] opacity-100" : "border-white/10 opacity-50 hover:opacity-75"}`}
              style={{ background: i === current ? "hsl(328,85%,50%,0.15)" : "hsl(0,0%,10%)" }}>
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PitchDeck;
